import { createSelector } from "@reduxjs/toolkit";

import type { TOrgInfo } from "@/types";
import { accountsApi } from "@api/accounts";
import { environment } from "@environment";
import moment from "moment";

const TESTING_GROUP_A = "A - testing group";
const TESTING_GROUP_B = "B - testing group";

export const getAccountQuery = accountsApi.endpoints.getOrgInfo.select({});
export const getAccount = createSelector(
  getAccountQuery,
  (account) => account.data,
);

export const getAccountId = createSelector(
  getAccount,
  (account) => account?._id,
);

export const getIsPasswordEnabled = createSelector(getAccount, (account) => {
  if (account?.passwordEnabled) {
    return account.passwordEnabled;
  }

  return false;
});

export const isPoweredByStorydocDisabled = createSelector(
  getAccount,
  (account) => account?.hidePoweredByStorydoc,
);

export const getIsSubscribed = createSelector(getAccount, (account) => {
  if (!account) return false;
  return account.subscriptionId && account.subscriptionId !== null;
});

export const getIsSubscriptionCanceled = createSelector(
  getAccount,
  (account) => {
    return Boolean(account?.cancelAt);
  },
);

export const getSubscriptionCanceledAt = createSelector(
  getAccount,
  (account) => {
    return account?.cancelAt;
  },
);

export const getIsSubscriptionPaused = createSelector(getAccount, (account) => {
  return Boolean(account && account.subscriptionStatus === "paused");
});

export const getIsActivePayingWorkspace = createSelector(
  getIsSubscribed,
  getIsSubscriptionCanceled,
  getAccount,
  (isSubscribed, isSubscriptionCanceled, account) => {
    return isSubscribed && !isSubscriptionCanceled && account?.isSelfServ;
  },
);

export const getInvitationToken = createSelector(getAccount, (account) => {
  return account?.invitationToken;
});

export const getPaymentPending = createSelector(getAccount, (account) => {
  return account?.paymentPending;
});

export const getIsAppSumo = createSelector(getAccount, (account) => {
  if (!account) return false;

  return !!account.appsumoUuid;
});

export const getIsTrialPlan = createSelector(getAccount, (account) => {
  return account && account.plan === "trial";
});

export const getIsTrialEnded = createSelector(getAccount, (account) => {
  if (!account || isPaidFn(account)) return false;
  const now = moment(new Date());
  const end = moment(account.trialEnd);
  const duration = moment.duration(end.diff(now));
  const days = duration.asDays();
  return days < 0;
});

export const getSubscriptionPlan = createSelector(getAccount, (account) => {
  return getSubscriptionPlanFn(account);
});

export const getPlanPrice = createSelector(
  getSubscriptionPlan,
  (selectedPlan) => selectedPlan?.price || 0,
);

export const isPlan = (plan: "trial" | "starter" | "pro" | "team") =>
  createSelector(
    getAccount,
    getSubscriptionPlan,
    (account, subscriptionPlan) => {
      if (!account) return false;
      const paid = isPaidFn(account);
      if (plan === "team") return paid && !account.isSelfServ;
      if (plan === "trial") return !paid;
      return subscriptionPlan?.title?.toLowerCase() === plan;
    },
  );

export const currentPlanTitle = createSelector(
  getAccount,
  getSubscriptionPlan,
  (account, subscriptionPlan) => {
    if (!account) return "trial";
    const paid = isPaidFn(account);
    if (paid && !account.isSelfServ) return "team";
    if (!paid) return "trial";
    return subscriptionPlan?.title?.toLowerCase();
  },
);

export const getAccountSeats = createSelector(getAccount, (account) => {
  return getAccountSeatsFn(account);
});

export const getTestingGroup = createSelector(getAccount, (account) => {
  if (!account) return TESTING_GROUP_A;
  return (account.testingNumber || 2) % 2 === 0
    ? TESTING_GROUP_A
    : TESTING_GROUP_B;
});

export const getIsAGroup = createSelector(
  getTestingGroup,
  (testingGroup: any) => testingGroup === TESTING_GROUP_A,
);

export const isVerified = createSelector(
  getAccount,
  isPlan("team"),
  (account, isTeam) => {
    return account?.verifiedAt || isTeam;
  },
);

export const isFirstPublish = createSelector(
  getAccount,
  (account: TOrgInfo | null) => account?.firstPublish,
);

export const showAnalyticsPlaceholder = createSelector(
  getAccount,
  (account: TOrgInfo | null) =>
    account?.showAnalyticsPlaceholder ||
    (!account?.firstPublish &&
      !account?.firstVersionCreated &&
      !account?.analyticsViewId),
);

export const getIsStorydocDemoAccount = createSelector(getAccount, (account) =>
  Boolean(account?.title?.includes("@storydocdemo.com")),
);

export const getCustomDomain = createSelector(
  getAccount,
  (account) => account?.customDomainV2,
);

export const getIsWhitelabelDomain = createSelector(
  getAccount,
  (account) => account?.customDomainV2?.indexOf("storydoc.live") === -1,
);

export const getCustomDomainV2Pending = createSelector(
  getAccount,
  (account) => account?.customDomainV2Pending,
);

export const getIsWhitelabelDomainPending = createSelector(
  getAccount,
  (account) => account?.customDomainV2Pending?.indexOf("storydoc.live") === -1,
);

export const isAnnualPlan = createSelector(
  getSubscriptionPlan,
  (plan) => plan?.period === "Annually",
);

export const getIsEarlyBird = createSelector(getAccount, (account) => {
  if (!account) return false;
  const timeDiff = new Date().getTime() - new Date(account.createdAt).getTime();
  const minutesDiff = timeDiff / (1000 * 60);
  return minutesDiff <= 48 * 60;
});

export const getSubscriptionPaymentPreset = createSelector(
  getAccount,
  (account) => {
    if (!account) return null;
    return account.subscriptionPaymentPreset;
  },
);

export const getSubscriptionPaymentPresetOverride = createSelector(
  getAccount,
  (account) => {
    if (!account) return null;
    return account.subscriptionPaymentPresetOverride;
  },
);

export const isAppSumo = createSelector(
  getAccount,
  (account) => !!account?.appsumoUuid,
);

export const isOptimizeVideoEnabled = createSelector(getAccount, (account) => {
  if (!account) return false;
  return account.optimizeVideoEnabled;
});

// ========== HELPER FUNCTIONS ==========
export const isPaidFn = (account: TOrgInfo | null) => {
  if (!account) return false;
  return account.plan === "paid" || account.plan === undefined;
};

export const isTrial = isPlan("trial");
export const isStarter = isPlan("starter");
export const isPro = isPlan("pro");
export const isTeam = isPlan("team");

export const getPlanById = (id: string | undefined) => {
  if (!id) return null;
  return environment.paddle.plans.find((plan) => plan.id === id);
};

export const isSubscriptionExpired = (trialEnd: Date | undefined) => {
  if (!trialEnd) return false;
  const now = moment(new Date());
  const end = moment(trialEnd);
  const duration = moment.duration(end.diff(now));
  const days = duration.asDays();
  return days < 0;
};

export const getSubscriptionPlanFn = (account: TOrgInfo | null) => {
  if (!account) return null;
  if (account?.appsumoUuid) {
    return environment.appSumo.plans.find(
      (plan) => plan.id === account.appsumoPlanId,
    );
  }
  return environment.paddle.plans.find(
    (plan) => plan.id === account.subscriptionPlanId,
  );
};

export const getAccountSeatsFn = (account: TOrgInfo | null) => {
  const paddlePlan = getSubscriptionPlanFn(account);
  const currentPlan = paddlePlan;
  let additionalSeats = 0;
  let freeSeats = 0;

  const planLimit = currentPlan?.seats ? currentPlan.seats : 1;

  if (account) {
    additionalSeats = account.additionalSeats
      ? Number.parseInt(account.additionalSeats)
      : 0;
    freeSeats = account.planProperties
      ? account.planProperties.freeSeats || 0
      : 0;
  }

  return { planLimit, additionalSeats, freeSeats };
};
